import { Trans } from "@lingui/react/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue, useSetAtom } from "jotai";
import React, { useEffect } from "react";
import { useFragment } from "react-relay";

import {
  Box,
  HStack,
  Text,
} from "@whoosh/design-system/src/components/primitive";

import {
  allowedGroupSizesAtom,
  currentSignUpRowAtom,
  isStaffAtom,
} from "../../atoms/eventSignUpProcess";
import { remainingEventCapacityAtom } from "../../atoms/eventSignUpProcess";
import { GroupRegistrationDisplay_event$key } from "./__generated__/GroupRegistrationDisplay_event.graphql";
import { FilledRegistrationSpot } from "./components/FilledRegistrationSpot";
import { RequiredSpot } from "./components/RequiredSpot";
import { getRequiredSpots } from "./utils/getRequiredSpots";

const MAX_VISIBLE_SPOTS = 6;
const UNLIMITED_CAPACITY = 1000;

type GroupRegistrantsDisplayProps = {
  isNewSignUp?: boolean;
  fragmentRef: GroupRegistrationDisplay_event$key;
};

export const GroupRegistrationDisplay = ({
  isNewSignUp,
  fragmentRef,
}: GroupRegistrantsDisplayProps) => {
  const event = useFragment(GroupRegistrantsDisplayFragmentString, fragmentRef);

  const allowedGroupSizes = useAtomValue(allowedGroupSizesAtom);
  const signUpRow = useAtomValue(currentSignUpRowAtom);
  const setRemainingEventCapacity = useSetAtom(remainingEventCapacityAtom);
  const isStaff = useAtomValue(isStaffAtom);

  const { minMemberCount, minGuestCount, spotsAvailable, capacityType } = event;

  const registrations =
    signUpRow?.registrations.map((r, i) => {
      return { ...r, index: i };
    }) ?? [];

  const remainingCapacityInEvent =
    isNewSignUp || isStaff
      ? UNLIMITED_CAPACITY
      : capacityType === "ATTENDEES"
      ? spotsAvailable && spotsAvailable > 0
        ? spotsAvailable
        : 0
      : UNLIMITED_CAPACITY;

  useEffect(() => {
    setRemainingEventCapacity(remainingCapacityInEvent);
  }, [remainingCapacityInEvent, setRemainingEventCapacity]);

  const supportsPublic = !!event.eventSeries?.facility?.club?.supportsPublic;

  const minGroupSize = allowedGroupSizes[0] ?? 0;
  const maxGroupSize = allowedGroupSizes[allowedGroupSizes.length - 1] ?? 0;

  const requiredSlots = getRequiredSpots({
    minGuestCount,
    minMemberCount,
    allRegistrations: registrations,
    supportsPublic,
    minGroupSize,
  });

  const totalSlots = registrations.length + requiredSlots.length;
  const shouldShowOverflow = totalSlots > MAX_VISIBLE_SPOTS;
  const visibleSlots = shouldShowOverflow ? MAX_VISIBLE_SPOTS : totalSlots;
  const additionalSpotsAvailable = maxGroupSize - totalSlots;
  const hiddenSlots = totalSlots - MAX_VISIBLE_SPOTS;
  const overflowCount = hiddenSlots + additionalSpotsAvailable;

  // Calculate how many filled registrations and required slots to show
  const visibleRegistrations = registrations.slice(
    0,
    Math.min(registrations.length, visibleSlots)
  );

  const visibleRequiredSlots = requiredSlots.slice(
    0,
    Math.max(0, visibleSlots - registrations.length)
  );

  const showMaxGroupSize = maxGroupSize > minGroupSize;

  return (
    <Box
      borderBottomColor="outline-default"
      borderBottomWidth={1.5}
      py={4}
      px={6}
      gap={3}
      justifyContent="center"
      alignItems="center"
    >
      <HStack gap={2} justifyContent="center">
        {visibleRegistrations.map((r, index) => {
          return (
            <FilledRegistrationSpot key={`${r.id}-${index}`} registration={r} />
          );
        })}

        {visibleRequiredSlots.map((spotType, i) => (
          <RequiredSpot
            type={spotType}
            index={i}
            key={`required-${spotType}-${i}`}
          />
        ))}

        {shouldShowOverflow && (
          <Box
            width={40}
            height={40}
            bg="surface-secondary"
            borderRadius="1"
            alignItems="center"
            justifyContent="center"
          >
            <Text variant="mainTiny" color="text-secondary">
              +{overflowCount}
            </Text>
          </Box>
        )}
      </HStack>
      {showMaxGroupSize ? (
        <Text variant="mainTiny" color="text-secondary">
          <Trans>Max group size</Trans>: {maxGroupSize}
        </Text>
      ) : null}
    </Box>
  );
};

const GroupRegistrantsDisplayFragmentString = graphql`
  fragment GroupRegistrationDisplay_event on Event {
    spotsAvailable
    capacityType
    allowedGroupSizes
    minGuestCount
    minMemberCount
    maxGuestCount
    maxMemberCount
    memberSignUpProxyAllowed
    eventSeries {
      facility {
        club {
          supportsPublic
        }
      }
    }
  }
`;
